import type { FormEvent } from 'react';
import React, { useState } from 'react';
import {
  InputField,
  NotificationWarning,
  SolidButton,
} from '@noths/polaris-client-ribbons-design-system';

import { useCommunicationPreferences } from 'src/hooks/useCommunicationPreferences/useCommunicationPreferences';
import type { Environment } from 'src/types/environment';
import * as tracking from './tracking';
import * as styles from './NewsletterSignUpForm.styles';

const PRIVACY_URL = 'https://www.notonthehighstreet.com/about/privacy';

const isValidEmailAddress = (email: string) => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
};

export const NewsletterSignUpForm = ({ env }: { env: Environment }) => {
  const { hasSignUpError, loading, onSignUp } = useCommunicationPreferences(env);
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState('');

  const handleChange = (evt: FormEvent<HTMLInputElement>) => setValue(evt.currentTarget.value);

  const handleSubmit = (evt: FormEvent) => {
    evt.preventDefault();
    setFieldError('');
    const email = value.trim();
    if (!email) {
      setFieldError('Email address is required');
      return;
    }
    if (!isValidEmailAddress(email)) {
      setFieldError('Email address is not valid');
      return;
    }

    onSignUp(email);
  };

  return (
    <div>
      <form css={styles.form} onSubmit={handleSubmit} noValidate>
        <h3 css={styles.heading}>Get first dibs</h3>
        <p css={styles.subHeading}>Sign up for sales, discounts and small business stuff</p>
        <div css={styles.formButtons}>
          <div>
            <InputField
              label="Newsletter signup"
              labelHidden
              id="newsletter-signup"
              onChange={handleChange}
              placeholder="Enter your email address"
              type="email"
              value={value}
              disabled={loading}
              error={fieldError}
            />
          </div>
          <div>
            <SolidButton fullWidth disabled={loading} loading={loading}>
              Subscribe
            </SolidButton>
          </div>
        </div>
        {hasSignUpError && (
          <NotificationWarning>Subscribing failed, try again later</NotificationWarning>
        )}
        <small css={styles.privacyPolicy}>
          See our{' '}
          <a
            css={styles.privacyPolicy}
            onClick={tracking.sendPrivacyPolicySelectedEvent}
            href={PRIVACY_URL}
          >
            privacy policy
          </a>{' '}
          to understand how we process your personal data to send you marketing emails
        </small>
      </form>
    </div>
  );
};
